import { Dialog, Stack, Text } from "@mantine/core"
import { FileWithPath, MIME_TYPES } from "@mantine/dropzone"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import {
  Button,
  DataFetchWrapper,
  EqualWidthGrid,
  SectionHeader,
  DocumentUploader,
  DEFAULT_MAX_SIZE,
} from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { IngestionsList } from "~/pages/Brand/DataTransfer/Import/IngestionsList"
import { config } from "~/pages/Brand/DataTransfer/config.ts"
import {
  useIngestionsQuery,
  useUploadIngestionFileMutation,
} from "~/pages/Brand/DataTransfer/hooks.ts"

export const DataTransferImport = () => {
  const { t } = useTranslation(["dataTransfer", "common"])
  const [opened, { toggle, close }] = useDisclosure(false)
  const [file, setFile] = useState<FileWithPath | null>(null)

  const ingestionsQuery = useIngestionsQuery()

  const { mutateAsync: uploadIngestionFile, isLoading: isUploading } =
    useUploadIngestionFileMutation()

  const confirm = (file: FileWithPath): void => {
    toggle()
    setFile(file)
  }

  const cancel = (): void => {
    close()
    notifications.show({
      title: t("upload.erase"),
      message: "",
    })
  }

  const uploadFile = async () => {
    if (!file) return
    await uploadIngestionFile(file)
    close()
    notifications.show({
      title: t("upload.success"),
      message: "",
    })
  }

  return (
    <Stack>
      <Dialog
        opened={opened}
        onClose={close}
        size="lg"
        radius="md"
        position={{ top: "40%", left: "40%" }}
      >
        <Text size="sm" mb="xs" style={{ fontWeight: "bold" }}>
          {t("upload.info")}
        </Text>
        <EqualWidthGrid>
          <Button
            onClick={cancel}
            fullWidth={true}
            variant="secondary-critical"
          >
            {t("upload.cancel")}
          </Button>
          <Button onClick={uploadFile} fullWidth={true}>
            {t("upload.confirm")}
          </Button>
        </EqualWidthGrid>
      </Dialog>
      <Stack>
        <SectionHeader
          mainCta={
            <Button component="a" href={config.DATA_TRANSFER_TEMPLATE}>
              {t("dlLatestTemplate")}
            </Button>
          }
        />

        <DocumentUploader
          accept={[MIME_TYPES.csv, MIME_TYPES.xls, MIME_TYPES.xlsx]}
          maxSize={200 * 1024 ** 2}
          onDrop={(files) => confirm(files[0])}
          isLoading={isUploading}
          title={t("common:documentUpload.title")}
          helpText={t("common:documentUpload.helptext", {
            maxSize: DEFAULT_MAX_SIZE,
          })}
        />
        <DataFetchWrapper query={ingestionsQuery}>
          {({ data }) => <IngestionsList ingestions={data} />}
        </DataFetchWrapper>
      </Stack>
    </Stack>
  )
}
